import { VOICE } from "./voice";

/**
 * Definitions Challenge Fix:
 * - UI is likely doing exact-string lookups: DEFINITIONS[key]
 * - We add ALIASES so variations like "Hypertrophy" / "hypertrophy" / "hypertrophy " resolve.
 * - We also standardize phase naming to match Scott’s House metaphor.
 */

const base = {
  // -----------------------
  // 1) PLANNING / ASSESSMENT (Survey)
  // -----------------------
  "Movement screen": {
    id: "movement_screen",
    phase: "Planning",
    title: "Movement Screen",
    subtitle: "10-Point Structural Audit",
    tags: ["Safety", "Baseline", "10 min"],
    angle: "Find the leak before you add load.",
    logic:
      "Most pain isn’t ‘weakness’—it’s compensation. We screen first so your program fits your joints, not your ego.",
    protocolLabel: "What we check",
    protocol: [
      "Overhead squat check (ankle/hip mobility)",
      "Shoulder clearing test (impingement risk)",
      "Active straight-leg raise (hamstring/core)",
      "Rotary stability (core control)",
    ],
    proTip: "Film 3 reps. Feel lies. Video doesn’t.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Lifestyle review": {
    id: "lifestyle_review",
    phase: "Planning",
    title: "Lifestyle & Habit Review",
    subtitle: "Finding the Friction",
    tags: ["Sleep", "Stress", "Recovery"],
    angle: "Training only works if you can recover.",
    logic:
      "Your body adapts between sessions, not during them. If sleep, stress, or nutrition are off, the perfect program still fails.",
    protocolLabel: "What we review",
    protocol: [
      "Sleep: hours vs. quality",
      "Protein: baseline target",
      "Stress load: work/life bandwidth",
    ],
    proTip: "I can program around chaos—but only if I can see it.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Injury history": {
    id: "injury_history",
    phase: "Planning",
    title: "Injury History",
    subtitle: "Respecting the Scars",
    tags: ["Safety", "History"],
    angle: "Old injuries write your movement habits.",
    logic:
      "Past injuries leave protective patterns—guarding, asymmetry, limited range. We map them so progress doesn’t poke the bear.",
    protocolLabel: "We document",
    protocol: [
      "Surgeries / major events timeline",
      "Recurring ache patterns",
      "Range limits + what triggers symptoms",
    ],
    proTip: "Pain is information. We treat it like data.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // 2) CLEAR LAND (Site Prep)
  // -----------------------
  "Mobility work": {
    id: "mobility_work",
    phase: "Clear Land",
    title: "Mobility Work",
    subtitle: "Clear the Range",
    tags: ["ROM", "Joints"],
    angle: "You can’t build on a stuck joint.",
    logic:
      "If a joint is stuck, something else pays the price. We clear restriction before we load the pattern.",
    protocolLabel: "Focus areas",
    protocol: [
      "Thoracic spine (desk posture)",
      "Hip flexors (sitting)",
      "Ankle dorsiflexion (squat depth)",
    ],
    proTip: "Motion is lotion.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  Correctives: {
    id: "correctives",
    phase: "Clear Land",
    title: "Correctives",
    subtitle: "The Unsexy Work That Prevents Setbacks",
    tags: ["Pre-hab", "Balance"],
    angle: "Earn the right to go heavy.",
    logic:
      "These are your ‘site prep’ drills—small, targeted movements that wake up stabilizers so the big lifts stay safe.",
    protocolLabel: "Daily vitamins",
    protocol: [
      "Clamshells / band walks",
      "Face pulls",
      "Single-leg balance drills",
    ],
    proTip: "If you skip this, you hit a ceiling.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Sleep Rhythm": {
    id: "sleep_rhythm",
    phase: "Clear Land",
    title: "Sleep Rhythm",
    subtitle: "The Night Shift Crew",
    tags: ["Recovery", "Hormones"],
    angle: "The crew builds while you’re off-site.",
    logic:
      "You don’t get stronger in the gym. You get stronger after—when the crew shows up at night to repair, rebuild, and adapt. No sleep = no crew.",
    protocolLabel: "The 3–2–1 rule",
    protocol: [
      "3 hours before bed: stop heavy food",
      "2 hours before bed: stop work / problem-solving",
      "1 hour before bed: dim lights + reduce screens",
    ],
    proTip: "Same wake time beats a perfect bedtime.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // 3) BLUEPRINTS (Defaults)
  // -----------------------
  "Structured plan": {
    id: "structured_plan",
    phase: "Blueprints",
    title: "Structured Plan",
    subtitle: "Eliminating Decision Fatigue",
    tags: ["Systems", "Clarity"],
    angle: "Remove negotiation. Increase execution.",
    logic:
      "Most people don’t fail from effort—they fail from inconsistency. A plan reduces choices so you can stack reps across weeks.",
    protocolLabel: "How it’s built",
    protocol: [
      "4-week blocks",
      "Clear sets/reps",
      "Rest timers + progression rules",
    ],
    proTip: "Don’t improvise. Execute.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Progressive overload": {
    id: "progressive_overload",
    phase: "Blueprints",
    title: "Progressive Overload",
    subtitle: "The Math Behind Results",
    tags: ["Progress", "Data"],
    angle: "Same stimulus = same body.",
    logic:
      "Strength and muscle are adaptations. If the stimulus never increases, your body has no reason to change.",
    protocolLabel: "Ways we progress",
    protocol: [
      "Add weight",
      "Add reps",
      "Improve tempo",
      "Reduce rest—carefully",
    ],
    proTip: "Win by 1%. Stack it for 8 weeks.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Pain-aware": {
    id: "pain_aware",
    phase: "Blueprints",
    title: "Pain-Aware Training",
    subtitle: "Progress Without Flare-Ups",
    tags: ["Joint-safe", "Sustainable"],
    angle: "We train around pain, not through it.",
    logic:
      "Pain spikes kill momentum. We choose ranges, angles, and progressions that build capacity while keeping symptoms quiet.",
    protocolLabel: "Guardrails",
    protocol: [
      "Pain scale rule (0–3 = OK, 4+ = adjust)",
      "Swap patterns, not effort (hinge ↔ bridge)",
      "Progress range before load",
    ],
    proTip: "Consistency—not hero days—wins.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // 4) FOUNDATION
  // -----------------------
  "Core stability": {
    id: "core_stability",
    phase: "Foundation",
    title: "Core Stability",
    subtitle: "Anti-Movement Strength",
    tags: ["Foundation", "Spine"],
    angle: "A strong core is braking power.",
    logic:
      "Real core training protects your spine while arms/legs move. That’s what keeps squats, hinges, and carries joint-friendly.",
    protocolLabel: "Staples",
    protocol: [
      "Deadbug (anti-extension)",
      "Pallof press (anti-rotation)",
      "Carries",
    ],
    proTip: "If your back works, your core didn’t.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Form & posture": {
    id: "form_posture",
    phase: "Foundation",
    title: "Form & Posture",
    subtitle: "Clean Reps First",
    tags: ["Technique", "Longevity"],
    angle: "Technique is joint insurance.",
    logic:
      "Good form makes the target muscle do the work and keeps joints from taking the bill. We earn load with repeatable reps.",
    protocolLabel: "What we cue",
    protocol: [
      "Brace + breath",
      "Stack ribs over pelvis",
      "Control the lowering phase",
    ],
    proTip: "Form isn’t perfection. It’s repeatability.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Protein baseline": {
    id: "protein_baseline",
    phase: "Foundation",
    title: "Protein Baseline",
    subtitle: "The Building Materials",
    tags: ["Nutrition", "Recovery"],
    angle: "Training is the signal. Protein is the lumber.",
    logic:
      "Training tells your body what to build. Protein supplies the materials. Without enough, recovery slows and results stall.",
    protocolLabel: "Baseline targets",
    protocol: [
      "0.7–1.0g per lb goal bodyweight",
      "30–40g per meal",
      "Prioritize breakfast protein",
    ],
    proTip: "A strong breakfast changes the whole day.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // 5) FINISH WORK
  // -----------------------
  Hypertrophy: {
    id: "hypertrophy",
    phase: "Finish Work",
    title: "Hypertrophy",
    subtitle: "Building the Structure",
    tags: ["Muscle", "Resilience"],
    angle: "This is where the frame starts to look like a house.",
    logic:
      "Muscle isn’t just for looks. It protects joints, improves blood sugar, and builds a buffer against aging. It’s the structure that makes everything else easier.",
    protocolLabel: "How we build it",
    protocol: [
      "Moderate reps (8–15)",
      "Near-failure effort (1–2 reps in reserve)",
      "Full range you can control",
    ],
    proTip: "The goal isn’t to survive the set—it's to control it.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Body Comp": {
    id: "body_comp",
    phase: "Finish Work",
    title: "Body Composition",
    subtitle: "Trim, Paint, and Detail",
    tags: ["Nutrition", "Aesthetics"],
    angle: "Reveal the work you already built.",
    logic:
      "We don’t chase extremes. We use small, sustainable adjustments so the strength and structure you built actually shows.",
    protocolLabel: "Levers we pull",
    protocol: [
      "Small deficit (if needed)",
      "More steps",
      "Higher protein + simpler meals",
    ],
    proTip: "You don’t need perfect—just consistent.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Zone 2 Cardio": {
    id: "zone_2",
    phase: "Finish Work",
    title: "Zone 2 Cardio",
    subtitle: "Electrical + Plumbing (Efficiency)",
    tags: ["Heart", "Recovery"],
    angle: "Make the house run smoother.",
    logic:
      "Zone 2 builds your engine. It improves energy production and recovery so you bounce back faster—between sets and between sessions.",
    protocolLabel: "The sweet spot",
    protocol: ["Nasal breathing", "Conversational pace", "30–45 minutes"],
    proTip: "If you can’t talk, you’re not in Zone 2.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // 6) MAINTENANCE
  // -----------------------
  "Recovery Cycles": {
    id: "recovery_cycles",
    phase: "Maintenance",
    title: "Recovery Cycles",
    subtitle: "Preventing Rot",
    tags: ["Longevity", "Sustainability"],
    angle: "If you never maintain it, it breaks.",
    logic:
      "We don’t grind 52 weeks a year. Recovery cycles protect connective tissue, keep hormones steady, and prevent the slow drift into aches and burnout.",
    protocolLabel: "The rhythm",
    protocol: [
      "Build for 3 weeks",
      "Deload for 1 week",
      "Seasonal reset blocks as needed",
    ],
    proTip: "Rest isn’t quitting. It’s maintenance.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Deload Weeks": {
    id: "deload_weeks",
    phase: "Maintenance",
    title: "Deload Weeks",
    subtitle: "Scheduled Inspections",
    tags: ["Safety", "Reset"],
    angle: "Back off to speed up.",
    logic:
      "A deload isn’t a week off. It’s an inspection week: perfect reps, lower load, reduced volume. It clears fatigue so progress can continue.",
    protocolLabel: "Deload rules",
    protocol: ["Same movements", "Half the sets", "Half the load (or effort)"],
    proTip: "Leave the gym feeling better than you arrived.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  "Life Integration": {
    id: "life_integration",
    phase: "Maintenance",
    title: "Life Integration",
    subtitle: "Living in the House",
    tags: ["Real life", "Consistency"],
    angle: "The plan must survive a bad week.",
    logic:
      "Fitness fails when it fights your life. We build minimum standards and quick pivots so momentum stays intact when work, travel, or family gets loud.",
    protocolLabel: "The pivots",
    protocol: [
      "Hotel room plan",
      "20-minute emergency session",
      "Maintenance minimum standard",
    ],
    proTip: "Never miss twice.",
    cta: { label: VOICE.ctas.acknowledge },
  },

  // -----------------------
  // DEFAULT FALLBACK
  // -----------------------
  DEFAULT: {
    id: "default",
    phase: "Protocol",
    title: "System Protocol",
    subtitle: "Evidence-Based Standard",
    tags: ["Protocol", "Standard"],
    angle: VOICE.motifs.joints,
    logic:
      "Every piece is selected for maximum ROI with minimum joint stress. Simple, repeatable, measurable.",
    protocolLabel: "Standards",
    protocol: [
      "Consistency over intensity",
      "Form over weight",
      "Long-term health over short-term hacks",
    ],
    proTip: "Trust the process.",
    cta: { label: VOICE.ctas.acknowledge },
  },
};

/**
 * Alias layer: resolves common variations and case differences.
 * This prevents “Definitions Challenge” UI failures without requiring UI changes.
 */
const ALIASES = {
  // Case / punctuation / spacing variants
  "movement screen": "Movement screen",
  "Movement Screen": "Movement screen",

  "lifestyle review": "Lifestyle review",
  "Lifestyle Review": "Lifestyle review",

  "injury history": "Injury history",
  "Injury History": "Injury history",

  "mobility work": "Mobility work",
  "Mobility Work": "Mobility work",

  correctives: "Correctives",
  Correctives: "Correctives",

  "sleep rhythm": "Sleep Rhythm",
  "Sleep rhythm": "Sleep Rhythm",
  "Sleep Rhythm": "Sleep Rhythm",

  "structured plan": "Structured plan",
  "Structured Plan": "Structured plan",

  "progressive overload": "Progressive overload",
  "Progressive Overload": "Progressive overload",

  "pain aware": "Pain-aware",
  "pain-aware": "Pain-aware",
  "Pain-aware": "Pain-aware",
  "Pain Aware": "Pain-aware",

  "core stability": "Core stability",
  "Core Stability": "Core stability",

  "form & posture": "Form & posture",
  "Form & Posture": "Form & posture",
  "form and posture": "Form & posture",

  "protein baseline": "Protein baseline",
  "Protein Baseline": "Protein baseline",

  hypertrophy: "Hypertrophy",
  Hypertrophy: "Hypertrophy",

  "body comp": "Body Comp",
  "Body Comp": "Body Comp",
  "Body Composition": "Body Comp",

  "zone 2 cardio": "Zone 2 Cardio",
  "Zone 2 Cardio": "Zone 2 Cardio",
  "Zone2 Cardio": "Zone 2 Cardio",

  "recovery cycles": "Recovery Cycles",
  "Recovery Cycles": "Recovery Cycles",

  "deload weeks": "Deload Weeks",
  "Deload Weeks": "Deload Weeks",
  Deload: "Deload Weeks",

  "life integration": "Life Integration",
  "Life Integration": "Life Integration",
};

export const DEFINITIONS = new Proxy(base, {
  get(target, prop) {
    if (typeof prop !== "string") return target[prop];
    const key = prop.trim();
    const canonical = ALIASES[key] || ALIASES[key.toLowerCase()] || key;
    return target[canonical] || target.DEFAULT;
  },
});
